<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
        <el-breadcrumb-item>实习生管理</el-breadcrumb-item>
        <el-breadcrumb-item>审单绩效统计</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <layout-panel :collapse="false">
      <template slot="header">搜索</template>
      <el-form :model="search"
               :rules="formRules"
               ref="searchForm"
               label-width="96px"
               label-position="right"
               size="medium"
               @submit.native.prevent="onSearch()">
        <div class="re-el-form__flex">
          <el-form-item label="出勤月份"
                        prop="stat_month">
            <el-date-picker class="width-max"
                            v-model="search.stat_month"
                            type="month"
                            value-format="yyyy-MM"
                            placeholder="请选择出勤月份"
                            clearable>
            </el-date-picker>
          </el-form-item>
          <el-form-item label="客户"
                        prop="customerid">
            <remote-select class="width-max"
                           v-model="search.customerid"
                           clearable
                           id-key="number"
                           label-key="name"
                           value-key="id"
                           placeholder="输入客户名称/编号搜索"
                           :query-request="getCustomerTable"
                           :find-request="getCustomerDetail"
                           @change="onCustomerChange">
            </remote-select>
          </el-form-item>
          <el-form-item label="项目名称"
                        prop="projectid">
            <remote-select class="width-max"
                           :key="projectSelectKey"
                           v-model="search.projectid"
                           clearable
                           id-key="number"
                           label-key="name"
                           value-key="id"
                           placeholder="输入项目名称/编号搜索"
                           :query-request="queryProjectTable"
                           :find-request="getProjectDetail"
                           @change="onProjectChange">
            </remote-select>
          </el-form-item>
          <el-form-item label="绩效分">
            <div class="score-range">
              <el-form-item prop="score_min"
                            class="score-range-item">
                <el-input v-model="search.score_min"
                          clearable
                          placeholder="最低分"></el-input>
              </el-form-item>
              <span>~</span>
              <el-form-item prop="score_max"
                            class="score-range-item">
                <el-input v-model="search.score_max"
                          clearable
                          placeholder="最高分"></el-input>
              </el-form-item>
            </div>
          </el-form-item>
          <el-form-item label="实习生"
                        prop="intern_user_id">
            <remote-select class="width-max"
                           v-model="search.intern_user_id"
                           clearable
                           id-key="id"
                           label-key="name"
                           value-key="id"
                           keyword-key="name"
                           placeholder="请输入账号名搜索"
                           :query-request="queryInternTableApi"
                           :find-request="getInternBaseInfoApi">
            </remote-select>
          </el-form-item>
        </div>
        <el-form-item>
          <el-button type="primary"
                     icon="el-icon-search"
                     native-type="submit">搜索
          </el-button>
          <el-button icon="el-icon-refresh"
                     @click="resetSearch()">重置
          </el-button>
          <el-button icon="sj-icon-export"
                     @click="onExport()">导出
          </el-button>
        </el-form-item>
      </el-form>
    </layout-panel>

    <layout-panel>
      <template slot="header">列表</template>
      <template slot="tools">
        <el-button type="primary"
                   size="small"
                   icon="el-icon-s-data"
                   :loading="recalculating"
                   @click="onManualRecalculate()">重新手动计算
        </el-button>
        <el-button type="primary"
                   size="small"
                   icon="el-icon-refresh"
                   @click="onRefresh()"></el-button>
      </template>
      <el-table ref="performanceTable"
                class="performance-table"
                :data="table"
                :default-sort="defaultSort"
                stripe
                highlight-current-row
                border
                @sort-change="onSortChange">
        <el-table-column prop="id"
                         label="ID"
                         width="80"></el-table-column>
        <el-table-column label="实习生"
                         width="140"
                         show-overflow-tooltip>
          <template slot-scope="scope">
            {{ internText(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column prop="stat_month"
                         label="出勤月份"
                         width="110"></el-table-column>
        <el-table-column prop="customer_name"
                         label="客户"
                         width="140"
                         show-overflow-tooltip></el-table-column>
        <el-table-column label="项目"
                         width="220"
                         show-overflow-tooltip>
          <template slot-scope="scope">
            {{ projectText(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column prop="audit_order_total"
                         label="审核工单总数"
                         width="130"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
        <el-table-column prop="is_full_attendance"
                         label="是否全勤"
                         width="110"
                         sortable="custom">
          <template slot-scope="scope">
            <span :class="Number(scope.row.is_full_attendance) === 1 ? 'attendance-yes' : 'attendance-no'">
              {{ Number(scope.row.is_full_attendance) === 1 ? '是' : '否' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="plan_order_total"
                         label="累计审核目标"
                         width="130"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
        <el-table-column prop="pass_count"
                         label="通过次数"
                         width="110"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
        <el-table-column prop="reject_count"
                         label="不通过次数"
                         width="120"
                         class-name="performance-num-cell"></el-table-column>
        <el-table-column prop="first_audit_count"
                         label="初审次数"
                         width="110"
                         class-name="performance-num-cell"></el-table-column>
        <el-table-column prop="review_audit_count"
                         label="复审次数"
                         width="110"
                         class-name="performance-num-cell"></el-table-column>
        <el-table-column prop="wrong_order_total"
                         label="误审单量"
                         width="110"
                         class-name="performance-num-cell"></el-table-column>
        <el-table-column prop="days_target_achieved"
                         label="目标达成天数"
                         width="130"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
        <el-table-column prop="days_target_onduty"
                         label="项目出勤天数"
                         width="130"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
        <el-table-column prop="target_score"
                         label="目标达成率（得分）"
                         width="220"
                         class-name="performance-num-cell"
                         label-class-name="score-header-cell"
                         sortable="custom">
          <template slot="header">
            <el-tooltip placement="bottom-start"
                        effect="dark"
                        popper-class="score-header-popper">
              <div slot="content">
                目标达成率 = 目标达成天数 / 项目出勤天数<br>
                分数折算：<br>
                ≥ 100%：45 分<br>
                90% ≤ 达成率 &lt; 100%：42 分<br>
                80% ≤ 达成率 &lt; 90%：38 分<br>
                70% ≤ 达成率 &lt; 80%：20 分<br>
                &lt; 70%：5 分
              </div>
              <span class="score-header">
                <span class="score-header-title">目标达成率（得分）</span>
                <span class="score-header-sub">目标达成天数/项目出勤天数</span>
              </span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ scope.row.achieve_rate_text }}
          </template>
        </el-table-column>
        <el-table-column prop="accuracy_score"
                         label="错误率（得分）"
                         width="200"
                         class-name="performance-num-cell"
                         label-class-name="score-header-cell"
                         sortable="custom">
          <template slot="header">
            <el-tooltip placement="bottom-start"
                        effect="dark"
                        popper-class="score-header-popper">
              <div slot="content">
                错误率 = 错审单量 / 项目审单量<br>
                分数折算：<br>
                错误率 ≤ 2%：45 分<br>
                2% &lt; 错误率 ≤ 5%：40 分<br>
                5% &lt; 错误率 ≤ 10%：35 分<br>
                错误率 &gt; 10%：0 分
              </div>
              <span class="score-header">
                <span class="score-header-title">错误率（得分）</span>
                <span class="score-header-sub">错审单量/项目审单量</span>
              </span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ scope.row.error_rate_text }}
          </template>
        </el-table-column>
        <el-table-column prop="attendance_score"
                         label="月度出勤得分"
                         width="130"
                         class-name="performance-num-cell"
                         label-class-name="score-header-cell"
                         sortable="custom">
          <template slot="header">
            <el-tooltip placement="bottom-start"
                        effect="dark"
                        popper-class="score-header-popper">
              <div slot="content">
                全勤：10 分<br>
                非全勤：0 分
              </div>
              <span class="score-header">
                <span class="score-header-title">月度出勤得分</span>
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="total_score"
                         label="审单绩效总分"
                         width="140"
                         class-name="performance-num-cell"
                         sortable="custom"></el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination background
                       @size-change="handleSizePageChange"
                       @current-change="handleCurrentPageChange"
                       :current-page="pagination.current_page"
                       :page-sizes="[20, 50, 100, 200]"
                       :page-size="pagination.per_page"
                       layout="total, sizes, prev, pager, next, jumper"
                       :total="pagination.total">
        </el-pagination>
      </div>
    </layout-panel>
  </div>
</template>

<script>
import table, { removeEmptyParams, trimParams } from '@core/mixins/table-v3'
import RemoteSelect from '@core/components/RemoteSelect.vue'

export default {
  components: {RemoteSelect},
  mixins: [table],
  data () {
    return {
      search: {
        stat_month: null,
        customerid: null,
        projectid: null,
        score_min: '',
        score_max: '',
        intern_user_id: null,
        order_by: null,
        order_dir: null,
      },
      searchFieldType: {
        customerid: String,
        projectid: String,
        intern_user_id: Number,
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
      },
      formRules: {
        score_min: [{validator: this.validateScoreBound, trigger: 'blur'}],
        score_max: [{validator: this.validateScoreBound, trigger: 'blur'}],
      },
      ignoreSortChange: false,
      recalculating: false,
      exportHeader: [
        {key: 'id', text: 'ID'},
        {key: 'intern_text', text: '实习生'},
        {key: 'stat_month', text: '出勤月份'},
        {key: 'customer_name', text: '客户'},
        {key: 'project_text', text: '项目'},
        {key: 'audit_order_total', text: '审核工单总数'},
        {key: 'is_full_attendance_text', text: '是否全勤'},
        {key: 'plan_order_total', text: '累计审核目标'},
        {key: 'pass_count', text: '通过次数'},
        {key: 'reject_count', text: '不通过次数'},
        {key: 'first_audit_count', text: '初审次数'},
        {key: 'review_audit_count', text: '复审次数'},
        {key: 'wrong_order_total', text: '误审单量'},
        {key: 'days_target_achieved', text: '目标达成天数'},
        {key: 'days_target_onduty', text: '项目出勤天数'},
        {key: 'achieve_rate_text', text: '目标达成率（得分）'},
        {key: 'error_rate_text', text: '错误率（得分）'},
        {key: 'attendance_score', text: '月度出勤得分'},
        {key: 'total_score', text: '审单绩效总分'},
      ],
    }
  },
  computed: {
    projectSelectKey () {
      return 'project-' + (this.search.customerid || 'all')
    },
    defaultSort () {
      if (!this.search.order_by || !this.search.order_dir) {
        return {}
      }
      return {
        prop: this.search.order_by,
        order: this.search.order_dir === 'asc' ? 'ascending' : 'descending',
      }
    },
  },
  methods: {
    ...request.map([
      'getCustomerTable',
      'getCustomerDetail',
      'getProjectTable',
      'getProjectDetail',
      'queryInternTableApi',
      'getInternBaseInfoApi',
      'queryInternAuditPerformanceApi',
    ]),
    getTableData (params) {
      return request.common('queryInternAuditPerformanceApi', params)
    },
    setTableData ({data}) {
      this.$set(this, 'table', data.data)
      this.setPagination(data)
      this.$nextTick(() => {
        this.syncTableSort()
      })
    },
    queryProjectTable (params) {
      const query = Object.assign({}, params)
      if (this.search.customerid) {
        query.customerid = this.search.customerid
      }
      return this.getProjectTable(query)
    },
    onCustomerChange () {
      this.search.projectid = null
    },
    onProjectChange (project) {
      if (!project || !project.customerid) {
        return
      }
      this.search.customerid = String(project.customerid)
    },
    onSortChange ({prop, order}) {
      if (this.ignoreSortChange) {
        return
      }
      const nextBy = order ? prop : null
      const nextDir = order === 'ascending' ? 'asc' : (order === 'descending' ? 'desc' : null)
      if (this.search.order_by === nextBy && this.search.order_dir === nextDir) {
        return
      }
      this.search.order_by = nextBy
      this.search.order_dir = nextDir
      this.onSearch()
    },
    onManualRecalculate () {
      const now = new Date()
      const month = this.search.stat_month || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      this.$confirm(`确认重新计算 ${month} 的审单绩效？`, '重新手动计算', {
        type: 'warning',
        confirmButtonText: '确认计算',
        cancelButtonText: '取消',
      }).then(() => {
        this.recalculating = true
        return request.common('recalculateInternAuditPerformanceApi', {stat_month: month}).then(({data}) => {
          this.$message.success(`${month} 已重新计算 ${data.count} 条`)
          this.onRefresh()
        }).finally(() => {
          this.recalculating = false
        })
      }).catch(() => {})
    },
    onExport () {
      this.$export('导出审单绩效统计', {
        fileHeader: this.exportHeader,
        total: this.pagination.total,
        request: this.queryInternAuditPerformanceApi,
        requestParams: removeEmptyParams(trimParams(_.cloneDeep(this.search))),
        getResponseData: (data) => {
          data.data = data.data.map(row => Object.assign({}, row, {
            intern_text: this.internText(row),
            project_text: this.projectText(row),
            is_full_attendance_text: Number(row.is_full_attendance) === 1 ? '是' : '否',
          }))
          return data
        }
      })
    },
    resetSearch () {
      this.$refs.searchForm.resetFields()
      this.search.order_by = null
      this.search.order_dir = null
      if (this.$refs.performanceTable) {
        this.$refs.performanceTable.clearSort()
      }
      this.onSearch()
    },
    internText (row) {
      const name = row.intern_name || ''
      const id = row.intern_user_id
      return name ? `${name}/${id}` : (id != null && id !== '' ? String(id) : '')
    },
    projectText (row) {
      const name = row.project_name || ''
      const number = row.project_number || ''
      if (name && number) {
        return `${name}/${number}`
      }
      return name || number
    },
    validateScoreBound (rule, value, callback) {
      if (value === '' || value === null || value === undefined) {
        callback()
        return
      }
      const text = String(value).trim()
      if (text === '' || isNaN(Number(text))) {
        callback(new Error('请输入数字'))
        return
      }
      if (rule.field !== 'score_max') {
        callback()
        return
      }
      const minText = String(this.search.score_min).trim()
      if (minText !== '' && !isNaN(Number(minText)) && Number(minText) > Number(text)) {
        callback(new Error('最低分不能大于最高分'))
        return
      }
      callback()
    },
    syncTableSort () {
      const table = this.$refs.performanceTable
      if (!table || !this.search.order_by || !this.search.order_dir) {
        return
      }
      const order = this.search.order_dir === 'asc' ? 'ascending' : 'descending'
      this.ignoreSortChange = true
      table.sort(this.search.order_by, order)
      this.ignoreSortChange = false
    },
  },
}
</script>

<style lang="scss"
       scoped>
.score-range {
  display: flex;
  align-items: center;

  .el-input {
    width: 120px;
  }

  span {
    margin: 0 8px;
    color: #909399;
  }
}

.score-range-item {
  margin-bottom: 0;
}

.attendance-yes,
.attendance-no {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
}

.attendance-yes {
  background: #E8FFF3;
  color: #00B42A;
}

.attendance-no {
  background: #FFF3E8;
  color: #FF7D00;
}

.score-header {
  display: inline-block;
  cursor: help;
  line-height: 1.2;
  vertical-align: middle;
}

.score-header-title {
  display: block;
  white-space: nowrap;
}

.score-header-sub {
  display: block;
  margin-top: 2px;
  color: #9ca3af;
  font-size: 10px;
  font-weight: normal;
  line-height: 1.2;
  white-space: nowrap;
}

/deep/ .score-header-cell .caret-wrapper {
  vertical-align: top;
}

/deep/ .performance-table {
  .el-table__header-wrapper th,
  .el-table__fixed-header-wrapper th {
    background-color: #f5f7fa;
  }

  .el-table__header th > .cell {
    white-space: nowrap;
  }

  .el-table__body tr.el-table__row--striped td {
    background-color: #fafafa;
  }

  .el-table__body td.performance-num-cell > .cell {
    font-weight: 600;
  }
}
</style>
<style lang="scss">
.score-header-popper {
  width: 256px;
  line-height: 1.625;
  font-size: 11px;
  font-weight: normal;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/views/audit-intern/pages/InternAuditPerformanceTable.vue